import { featuredInsight, latestInsights, trendingList } from "@/mocks/home";

function CardMeta({
  author,
  date,
  readTime,
  light,
}: {
  author: string;
  date: string;
  readTime: string;
  light?: boolean;
}) {
  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${
        light ? "text-background-300" : "text-foreground-500"
      }`}
    >
      <span className="inline-flex items-center gap-1">
        <i className="ri-user-3-line text-sm leading-none"></i>
        {author}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-calendar-line text-sm leading-none"></i>
        {date}
      </span>
      <span className="inline-flex items-center gap-1">
        <i className="ri-time-line text-sm leading-none"></i>
        {readTime}
      </span>
    </div>
  );
}

export default function FeatureFeed() {
  return (
    <section id="feed" className="w-full bg-background-50 py-12 md:py-16">
      <div className="px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-8">
            <div>
              <span className="inline-flex items-center gap-1.5 text-primary-600 text-sm font-medium">
                <i className="ri-fire-line text-base leading-none"></i>
                最新拉片
              </span>
              <h2 className="mt-2 font-heading font-bold text-foreground-950 text-2xl md:text-3xl">
                本期拆解与复核记录
              </h2>
            </div>
            <p className="text-foreground-600 text-sm max-w-md">
              每条分析都带时间码与置信度，可以照着顺序读，也可以只挑你关心的那个疑点。
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 flex flex-col gap-6 md:gap-8">
              <article className="group rounded-lg overflow-hidden bg-background-100 border border-background-200 cursor-pointer">
                <div className="relative w-full h-56 md:h-80 overflow-hidden">
                  <img
                    src={featuredInsight.cover}
                    alt={`${featuredInsight.title} 封面`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                    主推 · {featuredInsight.analysisType}
                  </span>
                  <span className="absolute bottom-4 right-4 px-2.5 py-1 rounded-md bg-background-950/70 text-background-100 text-xs">
                    {featuredInsight.confidence}
                  </span>
                </div>
                <div className="p-5 md:p-6">
                  <h3 className="font-heading font-bold text-foreground-950 text-lg md:text-2xl leading-snug group-hover:text-primary-600 transition-colors">
                    {featuredInsight.title}
                  </h3>
                  <p className="mt-3 text-foreground-600 text-sm leading-relaxed">
                    {featuredInsight.summary}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {featuredInsight.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-900 text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-5 pt-4 border-t border-background-200">
                    <CardMeta
                      author={featuredInsight.author}
                      date={featuredInsight.date}
                      readTime={featuredInsight.readTime}
                    />
                  </div>
                </div>
              </article>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {latestInsights.map((item) => (
                  <article
                    key={item.id}
                    className="group rounded-lg overflow-hidden bg-background-100 border border-background-200 cursor-pointer flex flex-col"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={item.cover}
                        alt={`${item.title} 封面`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className="absolute top-3 left-3 px-2 py-0.5 rounded bg-secondary-500 text-background-50 text-xs">
                        {item.category}
                      </span>
                    </div>
                    <div className="p-4 md:p-5 flex flex-col flex-1">
                      <h3 className="font-heading font-bold text-foreground-950 text-base leading-snug group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-foreground-600 text-xs md:text-sm leading-relaxed line-clamp-3 flex-1">
                        {item.summary}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {item.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded bg-background-200 text-foreground-700 text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="mt-4 pt-3 border-t border-background-200">
                        <CardMeta author={item.author} date={item.date} readTime={item.readTime} />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-foreground-950 text-lg flex items-center gap-2">
                    <i className="ri-bar-chart-2-line text-primary-600 text-base leading-none"></i>
                    阅读热榜
                  </h3>
                  <span className="text-foreground-500 text-xs">近 7 天</span>
                </div>
                <ul className="flex flex-col">
                  {trendingList.map((item, index) => (
                    <li key={item.id}>
                      <a
                        href="#feed"
                        className="flex items-start gap-3 py-3 border-b border-background-200 last:border-0 cursor-pointer group"
                      >
                        <span
                          className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded font-heading text-xs font-bold ${
                            index === 0
                              ? "bg-primary-500 text-background-50"
                              : "bg-background-200 text-foreground-700"
                          }`}
                        >
                          {item.rank}
                        </span>
                        <span className="flex-1 text-sm text-foreground-800 leading-snug group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </span>
                        <span className="text-foreground-500 text-xs whitespace-nowrap">
                          {item.heat}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-5 md:p-6">
                <h3 className="font-heading font-bold text-secondary-900 text-lg flex items-center gap-2">
                  <i className="ri-shield-check-line text-secondary-600 text-base leading-none"></i>
                  阅读前须知
                </h3>
                <p className="mt-3 text-secondary-900/80 text-sm leading-relaxed">
                  页面呈现的是可复核的观察点与置信度标注，不是对任何人的定性判断。
                  遇到「待核实」条目，请把它当作线索而不是结论。
                </p>
                <a
                  href="#method"
                  className="mt-4 inline-flex items-center gap-1.5 text-secondary-700 text-sm font-medium whitespace-nowrap cursor-pointer hover:text-secondary-600"
                >
                  了解分析方法论
                  <i className="ri-arrow-right-s-line text-base leading-none"></i>
                </a>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}